/*
 * Copyright 2018 The Kubeflow Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import BusyButton from '../atoms/BusyButton';
import DropzoneArea, { DropzoneAreaHandle } from '../atoms/DropzoneArea';
import Input from '../atoms/Input';
import { TextFieldProps } from '@mui/material/TextField';
import { padding, commonCss, zIndex, color } from '../Css';
import { stylesheet, classes } from 'typestyle';
import { ExternalLink } from '../atoms/ExternalLink';
import PrivateSharedSelector from './PrivateSharedSelector';
import { BuildInfoContext } from 'src/lib/BuildInfo';

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
} from '@mui/material';

const css = stylesheet({
  dropOverlay: {
    backgroundColor: color.lightGrey,
    border: '2px dashed #aaa',
    bottom: 0,
    left: 0,
    padding: '2.5em 0',
    position: 'absolute',
    right: 0,
    textAlign: 'center',
    top: 0,
    zIndex: zIndex.DROP_ZONE_OVERLAY,
  },
  fileError: {
    color: 'red',
    fontSize: 12,
    marginTop: 4,
  },
  root: {
    width: 500,
  },
});

export enum ImportMethod {
  LOCAL = 'local',
  URL = 'url',
}

interface UploadPipelineDialogProps {
  open: boolean;
  onClose: (
    confirmed: boolean,
    name: string,
    file: File | null,
    url: string,
    method: ImportMethod,
    isPrivatePipeline: boolean,
    description?: string,
  ) => Promise<boolean>;
}

interface UploadPipelineDialogState {
  busy: boolean;
  dropzoneActive: boolean;
  file: File | null;
  fileError: string;
  fileName: string;
  fileUrl: string;
  importMethod: ImportMethod;
  uploadPipelineDescription: string;
  uploadPipelineName: string;
  isPrivatePipeline: boolean;
}

export const PIPELINE_PACKAGE_ACCEPT = {
  'application/yaml': ['.yaml', '.yml'],
  'application/zip': ['.zip'],
  'application/gzip': ['.tar.gz'],
};

export const PIPELINE_PACKAGE_REJECT_MESSAGE =
  'Invalid file type. Supported formats: .yaml, .yml, .zip, .tar.gz';

/**
 * Secondary validator for react-dropzone's {@link useDropzone} hook.
 *
 * The accept map must include `application/gzip` so the native file picker
 * (File System Access API) correctly recognises `.tar.gz` files. However,
 * plain `.gz` files share the same MIME type and pass the built-in MIME
 * check. This validator rejects them before they reach `onDropAccepted`.
 */
export function pipelinePackageValidator(file: File): { code: string; message: string } | null {
  const name = file.name.toLowerCase();
  if (name.endsWith('.gz') && !name.endsWith('.tar.gz')) {
    return { code: 'invalid-extension', message: PIPELINE_PACKAGE_REJECT_MESSAGE };
  }
  if (name.endsWith('.tgz')) {
    return { code: 'invalid-extension', message: PIPELINE_PACKAGE_REJECT_MESSAGE };
  }
  return null;
}

class UploadPipelineDialog extends React.Component<
  UploadPipelineDialogProps,
  UploadPipelineDialogState
> {
  static contextType = BuildInfoContext;
  declare context: React.ContextType<typeof BuildInfoContext>;
  private _dropzoneRef = React.createRef<DropzoneAreaHandle>();

  constructor(props: any) {
    super(props);

    this.state = {
      busy: false,
      dropzoneActive: false,
      file: null,
      fileError: '',
      fileName: '',
      fileUrl: '',
      importMethod: ImportMethod.LOCAL,
      uploadPipelineDescription: '',
      uploadPipelineName: '',
      isPrivatePipeline: true,
    };
  }

  public render(): React.JSX.Element {
    const {
      dropzoneActive,
      file,
      fileError,
      fileName,
      fileUrl,
      importMethod,
      uploadPipelineName,
      busy,
    } = this.state;

    return (
      <Dialog
        id='uploadDialog'
        onClose={() => this._uploadDialogClosed(false)}
        open={this.props.open}
        classes={{ paper: css.root }}
      >
        <DialogTitle>Upload and name your pipeline</DialogTitle>
        <div className={padding(20, 'lr')}>
          {this.context?.apiServerMultiUser && (
            <PrivateSharedSelector
              onChange={(val) => {
                this.setState({
                  isPrivatePipeline: val,
                });
              }}
            ></PrivateSharedSelector>
          )}

          <div>Upload a pipeline package file from your computer or import one using a URL.</div>

          <div className={classes(commonCss.flex, padding(10, 'b'))}>
            <FormControlLabel
              id='uploadLocalFileBtn'
              label='Upload a file'
              checked={importMethod === ImportMethod.LOCAL}
              control={<Radio color='primary' />}
              onChange={() => this.setState({ importMethod: ImportMethod.LOCAL, fileError: '' })}
            />
            <FormControlLabel
              id='uploadFromUrlBtn'
              label='Import by URL'
              checked={importMethod === ImportMethod.URL}
              control={<Radio color='primary' />}
              onChange={() => this.setState({ importMethod: ImportMethod.URL, fileError: '' })}
            />
          </div>

          {importMethod === ImportMethod.LOCAL && (
            <React.Fragment>
              <DropzoneArea
                id='dropZone'
                data-testid='upload-pipeline-dropzone'
                aria-label='Pipeline package drop zone'
                onDrop={this._onDrop.bind(this)}
                onDropRejected={this._onDropRejected.bind(this)}
                onDragEnter={this._onDropzoneDragEnter.bind(this)}
                onDragLeave={this._onDropzoneDragLeave.bind(this)}
                accept={PIPELINE_PACKAGE_ACCEPT}
                validator={pipelinePackageValidator}
                style={{ position: 'relative' }}
                ref={this._dropzoneRef}
                inputProps={{ tabIndex: -1 }}
              >
                {dropzoneActive && <div className={css.dropOverlay}>Drop files..</div>}

                <div className={padding(10, 'b')}>You can also drag and drop the file here.</div>
                <DocumentationCompilePipeline />
                <Input
                  onChange={this.handleChange('fileName')}
                  value={fileName}
                  required={true}
                  label='File'
                  variant='outlined'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Button
                          color='secondary'
                          onClick={() => this._dropzoneRef.current!.open()}
                          style={{ padding: '3px 5px', margin: 0, whiteSpace: 'nowrap' }}
                        >
                          Choose file
                        </Button>
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
                {fileError && <div className={css.fileError}>{fileError}</div>}
              </DropzoneArea>
            </React.Fragment>
          )}

          {importMethod === ImportMethod.URL && (
            <React.Fragment>
              <div className={padding(10, 'b')}>URL must be publicly accessible.</div>
              <DocumentationCompilePipeline />
              <Input
                onChange={this.handleChange('fileUrl')}
                value={fileUrl}
                required={true}
                label='URL'
                variant='outlined'
              />
            </React.Fragment>
          )}

          <Input
            id='uploadFileName'
            label='Pipeline name'
            onChange={this.handleChange('uploadPipelineName')}
            required={true}
            autoFocus={true}
            value={uploadPipelineName}
            variant='outlined'
          />
        </div>

        {/* <Input label='Pipeline description'
          onChange={this.handleChange('uploadPipelineDescription')}
          value={uploadPipelineDescription} multiline={true} variant='outlined' /> */}

        <DialogActions>
          <Button id='cancelUploadBtn' onClick={() => this._uploadDialogClosed.bind(this)(false)}>
            Cancel
          </Button>
          <BusyButton
            id='confirmUploadBtn'
            onClick={() => this._uploadDialogClosed.bind(this)(true)}
            title='Upload'
            busy={busy}
            disabled={
              !uploadPipelineName || (importMethod === ImportMethod.LOCAL ? !file : !fileUrl)
            }
          />
        </DialogActions>
      </Dialog>
    );
  }

  public handleChange = (name: string) => (event: any) => {
    this.setState({
      [name]: (event.target as TextFieldProps).value,
    } as any);
  };

  private _onDropzoneDragEnter(): void {
    this.setState({ dropzoneActive: true });
  }

  private _onDropzoneDragLeave(): void {
    this.setState({ dropzoneActive: false });
  }

  private _onDrop(files: File[]): void {
    this.setState({
      dropzoneActive: false,
      fileError: '',
      file: files[0],
      fileName: files[0].name,
      uploadPipelineName: files[0].name.split('.')[0],
    });
  }

  private _onDropRejected(): void {
    this.setState({
      dropzoneActive: false,
      file: null,
      fileName: '',
      uploadPipelineName: '',
      fileError: PIPELINE_PACKAGE_REJECT_MESSAGE,
    });
  }

  private _uploadDialogClosed(confirmed: boolean): void {
    this.setState({ busy: true }, async () => {
      const success = await this.props.onClose(
        confirmed,
        this.state.uploadPipelineName,
        this.state.file,
        this.state.fileUrl.trim(),
        this.state.importMethod,
        this.state.isPrivatePipeline,
        this.state.uploadPipelineDescription,
      );
      if (success) {
        this.setState({
          busy: false,
          dropzoneActive: false,
          file: null,
          fileError: '',
          fileName: '',
          fileUrl: '',
          importMethod: ImportMethod.LOCAL,
          uploadPipelineDescription: '',
          uploadPipelineName: '',
        });
      } else {
        this.setState({ busy: false });
      }
    });
  }
}

export default UploadPipelineDialog;

export const DocumentationCompilePipeline: React.FC = () => (
  <div className={padding(10, 'b')}>
    For expected file format, refer to{' '}
    <ExternalLink href='https://www.kubeflow.org/docs/components/pipelines/v2/compile-a-pipeline/'>
      Compile Pipeline Documentation
    </ExternalLink>
    .
  </div>
);
